/**
 * Outbox - uniwersalna kolejka mutacji offline.
 * Zastępuje pending_expenses; obsługuje wszystkie POST/PUT/PATCH/DELETE.
 */

import { openDb } from "./db";
import type { OutboxEntry, OutboxMethod, OutboxStatus } from "./types";

const OUTBOX_STORE = "outbox";

export async function enqueue(
  method: OutboxMethod,
  path: string,
  body: Record<string, unknown>,
  clientId?: string,
  initialStatus: OutboxStatus = "pending",
): Promise<number> {
  const conn = await openDb();
  if (!conn) {
    throw new Error("IndexedDB not available");
  }

  try {
    const entry: Omit<OutboxEntry, "id"> = {
      client_id: clientId || crypto.randomUUID(),
      method,
      path,
      body,
      status: initialStatus,
      created_at: Date.now(),
      retry_count: 0,
    };

    return await new Promise((resolve, reject) => {
      const tx = conn.db.transaction(OUTBOX_STORE, "readwrite");
      const store = tx.objectStore(OUTBOX_STORE);
      const request = store.add(entry);

      request.onsuccess = () => {
        const id = request.result as number;
        console.log(`[outbox] Enqueued ${method} ${path}, id=${id}`);
        resolve(id);
      };

      request.onerror = () => {
        console.error(`[outbox] Failed to enqueue ${method} ${path}:`, request.error);
        reject(request.error);
      };
    });
  } finally {
    conn.close();
  }
}

export async function getAllPending(): Promise<OutboxEntry[]> {
  const pending = await getByStatus("pending");
  const syncing = await getByStatus("syncing");
  return [...pending, ...syncing].sort((a, b) => a.created_at - b.created_at);
}

async function getByStatus(status: OutboxStatus): Promise<OutboxEntry[]> {
  const conn = await openDb();
  if (!conn) return [];

  try {
    return await new Promise((resolve) => {
      const tx = conn.db.transaction(OUTBOX_STORE, "readonly");
      const store = tx.objectStore(OUTBOX_STORE);
      const index = store.index("status");
      const request = index.getAll(status);

      request.onsuccess = () => {
        resolve((request.result as OutboxEntry[]) || []);
      };

      request.onerror = () => {
        console.error(`[outbox] Failed to get ${status}:`, request.error);
        resolve([]);
      };
    });
  } finally {
    conn.close();
  }
}

export async function getAllFailed(): Promise<OutboxEntry[]> {
  const conn = await openDb();
  if (!conn) return [];

  try {
    return await new Promise((resolve) => {
      const tx = conn.db.transaction(OUTBOX_STORE, "readonly");
      const store = tx.objectStore(OUTBOX_STORE);
      const index = store.index("status");
      const request = index.getAll("failed");

      request.onsuccess = () => {
        resolve((request.result as OutboxEntry[]) || []);
      };

      request.onerror = () => {
        console.error("[outbox] Failed to get failed entries:", request.error);
        resolve([]);
      };
    });
  } finally {
    conn.close();
  }
}

export async function getAllNeedingPassword(): Promise<OutboxEntry[]> {
  const conn = await openDb();
  if (!conn) return [];

  try {
    return await new Promise((resolve) => {
      const tx = conn.db.transaction(OUTBOX_STORE, "readonly");
      const store = tx.objectStore(OUTBOX_STORE);
      const index = store.index("status");
      const request = index.getAll("needs_password");

      request.onsuccess = () => {
        resolve((request.result as OutboxEntry[]) || []);
      };

      request.onerror = () => {
        console.error("[outbox] Failed to get needs_password entries:", request.error);
        resolve([]);
      };
    });
  } finally {
    conn.close();
  }
}

export async function updateStatus(
  id: number,
  status: OutboxStatus,
  errorMessage?: string,
): Promise<void> {
  const conn = await openDb();
  if (!conn) return;

  try {
    const tx = conn.db.transaction(OUTBOX_STORE, "readwrite");
    const store = tx.objectStore(OUTBOX_STORE);
    const getRequest = store.get(id);

    await new Promise<void>((resolve, reject) => {
      getRequest.onsuccess = () => {
        const entry = getRequest.result as OutboxEntry | undefined;
        if (!entry) {
          resolve();
          return;
        }

        entry.status = status;
        if (status === "failed" || status === "needs_password") {
          entry.retry_count += 1;
          if (errorMessage) {
            entry.error_message = errorMessage;
          }
        }

        const putRequest = store.put(entry);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  } finally {
    conn.close();
  }
}

export async function deleteEntry(id: number): Promise<void> {
  const conn = await openDb();
  if (!conn) return;

  try {
    const tx = conn.db.transaction(OUTBOX_STORE, "readwrite");
    const store = tx.objectStore(OUTBOX_STORE);
    store.delete(id);

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => {
        console.log(`[outbox] Deleted entry ${id}`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    conn.close();
  }
}

export async function getOutboxCount(): Promise<number> {
  const conn = await openDb();
  if (!conn) return 0;

  try {
    return await new Promise((resolve) => {
      const tx = conn.db.transaction(OUTBOX_STORE, "readonly");
      const store = tx.objectStore(OUTBOX_STORE);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.error("[outbox] Failed to count:", request.error);
        resolve(0);
      };
    });
  } finally {
    conn.close();
  }
}

export async function clearOutbox(): Promise<void> {
  const conn = await openDb();
  if (!conn) return;

  try {
    const tx = conn.db.transaction(OUTBOX_STORE, "readwrite");
    const store = tx.objectStore(OUTBOX_STORE);
    store.clear();

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => {
        console.log("[outbox] Cleared all entries");
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    conn.close();
  }
}
